import type { BaseDataPoint } from "../charts";
import type { TileStore } from "./store";
import { tilesForViewport } from "./tiles";
import { hasTileData, type ResolutionLadder, type Tier, type TileRef, tileKey, type Viewport } from "./types";

export type Coverage = { earliestMs?: number; latestMs?: number; erroredTiles: number };
export type AssembledSeries = {
  line: BaseDataPoint[];
  band: BaseDataPoint[];
  lines: Record<string, BaseDataPoint[]>;
  renderTier: Tier | null;
  targetTier: Tier;
  isFallback: boolean;
  isStale: boolean;
  coverage: Coverage;
};

const emptyCoverage: Coverage = { erroredTiles: 0 };

function assembleTierRefs(
  refs: TileRef[],
  store: TileStore
): Pick<AssembledSeries, "line" | "band" | "lines" | "coverage"> {
  const lineByTs = new Map<number, BaseDataPoint>();
  const bandByTs = new Map<number, BaseDataPoint>();
  const linesByKey = new Map<string, Map<number, BaseDataPoint>>();
  let erroredTiles = 0;
  for (const ref of refs) {
    const entry = store.get(tileKey(ref.tierId, ref.index));
    if (!entry) continue;
    if (entry.error) erroredTiles += 1;
    if (!entry.data) continue;
    for (const p of entry.data.line) lineByTs.set(p.xValue.getTime(), p);
    for (const p of entry.data.band ?? []) bandByTs.set(p.xValue.getTime(), p);
    for (const [key, points] of Object.entries(entry.data.lines ?? {})) {
      let byTs = linesByKey.get(key);
      if (!byTs) {
        byTs = new Map<number, BaseDataPoint>();
        linesByKey.set(key, byTs);
      }
      for (const p of points) byTs.set(p.xValue.getTime(), p);
    }
  }
  const byTime = (a: BaseDataPoint, b: BaseDataPoint) => a.xValue.getTime() - b.xValue.getTime();
  const line = [...lineByTs.values()].sort(byTime);
  const band = [...bandByTs.values()].sort(byTime);
  const lines = Object.fromEntries(
    [...linesByKey.entries()].map(([key, byTs]) => [key, [...byTs.values()].sort(byTime)])
  );
  let earliestMs: number | undefined;
  let latestMs: number | undefined;
  for (const points of [line, band, ...Object.values(lines)]) {
    if (!points.length) continue;
    const first = points[0].xValue.getTime();
    const last = points[points.length - 1].xValue.getTime();
    earliestMs = Math.min(earliestMs ?? first, first);
    latestMs = Math.max(latestMs ?? last, last);
  }
  return {
    line,
    band,
    lines,
    coverage: {
      earliestMs,
      latestMs,
      erroredTiles,
    },
  };
}

function visibleRefsLoaded(viewport: Viewport, tier: Tier, store: TileStore, maxFutureMs?: number): boolean {
  const refs = tilesForViewport(viewport, tier, 0).filter((ref) => maxFutureMs == null || ref.startMs < maxFutureMs);
  if (refs.length === 0) return false;
  return refs.every((ref) => {
    const entry = store.get(tileKey(ref.tierId, ref.index));
    return entry != null && (entry.status === "loaded" || entry.status === "empty");
  });
}

function hasLoadedDataInViewport(viewport: Viewport, tier: Tier, store: TileStore): boolean {
  const refs = tilesForViewport(viewport, tier, 0);
  return refs.some((ref) => {
    const entry = store.get(tileKey(ref.tierId, ref.index));
    return entry?.status === "loaded" && hasTileData(entry.data);
  });
}

function prevOverlapsViewport(prev: AssembledSeries, viewport: Viewport): boolean {
  if (!hasTileData(prev)) return false;
  const earliest = prev.coverage.earliestMs;
  const latest = prev.coverage.latestMs;
  if (earliest == null || latest == null) return false;
  return earliest < viewport.endMs && latest > viewport.startMs;
}

export type AssembleSeriesArgs = {
  viewport: Viewport;
  targetTier: Tier;
  ladder: ResolutionLadder;
  store: TileStore;
  overscanTiles: number;
  prev?: AssembledSeries | null;
  prevViewport?: Viewport | null;
  maxFutureMs?: number;
};

export function assembleSeries({
  viewport,
  targetTier,
  ladder,
  store,
  overscanTiles,
  prev,
  prevViewport,
  maxFutureMs,
}: AssembleSeriesArgs): AssembledSeries {
  const targetReady = visibleRefsLoaded(viewport, targetTier, store, maxFutureMs);

  // An explicitly revalidated empty target is authoritative; an old coarser
  // cache must not resurrect measurements removed by a correction.
  const visibleEntries = tilesForViewport(viewport, targetTier, 0)
    .filter((ref) => maxFutureMs == null || ref.startMs < maxFutureMs)
    .map((ref) => store.get(tileKey(ref.tierId, ref.index)));
  const targetRevalidated = visibleEntries.every((entry) => entry?.revalidated && !entry.refreshing && !entry.error);
  const targetHasData = hasLoadedDataInViewport(viewport, targetTier, store);
  if (
    targetReady &&
    (!targetHasData || prev?.isFallback) &&
    visibleEntries.some((entry) => entry?.refreshing || entry?.error || entry?.revalidated === false) &&
    prev &&
    prev.targetTier.id === targetTier.id &&
    prevViewport?.startMs === viewport.startMs &&
    prevViewport?.endMs === viewport.endMs
  ) {
    return prev;
  }
  if (targetReady && (targetRevalidated || targetHasData)) {
    const refs = tilesForViewport(viewport, targetTier, overscanTiles);
    const { line, band, lines, coverage } = assembleTierRefs(refs, store);
    return { line, band, lines, renderTier: targetTier, targetTier, isFallback: false, isStale: false, coverage };
  }

  // Policy 2: walk toward coarser tiers for a loaded fallback that has data.
  // Runs before settling for an all-empty target, so a coarser tier still
  // holding points for this viewport wins over rendering an empty target.
  const targetIdx = ladder.findIndex((t) => t.id === targetTier.id);
  for (let i = targetIdx + 1; i < ladder.length; i++) {
    const coarserTier = ladder[i];
    if (hasLoadedDataInViewport(viewport, coarserTier, store)) {
      const refs = tilesForViewport(viewport, coarserTier, overscanTiles);
      const { line, band, lines, coverage } = assembleTierRefs(refs, store);
      return { line, band, lines, renderTier: coarserTier, targetTier, isFallback: true, isStale: false, coverage };
    }
  }

  // Policy 1b: target fully resolved but genuinely empty and no coarser tier has
  // data → render the (empty) target honestly rather than a stale previous series.
  if (targetReady) {
    const refs = tilesForViewport(viewport, targetTier, overscanTiles);
    const { line, band, lines, coverage } = assembleTierRefs(refs, store);
    return { line, band, lines, renderTier: targetTier, targetTier, isFallback: false, isStale: false, coverage };
  }

  // Policy 3: keep-previous.
  if (prev) {
    const overlaps = prevOverlapsViewport(prev, viewport);
    if (overlaps) {
      return { ...prev, targetTier, isFallback: true, isStale: false };
    }
    // Non-overlapping stale: visual placeholder with honest empty coverage.
    return {
      line: prev.line,
      band: prev.band,
      lines: prev.lines,
      renderTier: null,
      targetTier,
      isFallback: true,
      isStale: true,
      coverage: emptyCoverage,
    };
  }

  // Nothing available.
  return {
    line: [],
    band: [],
    lines: {},
    renderTier: null,
    targetTier,
    isFallback: false,
    isStale: false,
    coverage: emptyCoverage,
  };
}
